import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabaseClient';
import type { User, Session } from '@supabase/supabase-js';
import { 
  UserProfile,
  normalizeUserProfile,
  createAnonymousProfile,
  prepareProfileApiUpdate
} from '@/types/userProfile';
import { AuthEvents } from '@/utils/events';
import { logger } from '@/utils/logger';

// ------ Constants ------
const BUILD_ID = crypto.randomUUID();
const serverUrl = import.meta.env.VITE_BACKEND_SERVER_URL;
const API_URL = `${serverUrl}/v1`;

// ------ Types ------
type SubscriptionTier = 'anonymous' | 'free' | 'premium';

interface SubscriptionStatus {
  is_canceled: boolean;
  can_reactivate: boolean;
  active_until: string | null;
  is_active: boolean;
  subscription_tier?: string;
}

interface AuthContextProps {
  user: User | null;
  userProfile: UserProfile | null;
  session: Session | null;
  sessionId: string | null;
  anonymousToken: string | null;
  loading: boolean;
  isSubscribed: boolean;
  isAdmin: boolean;
  isAuthenticated: boolean;
  subscriptionStatus: SubscriptionStatus | null;
  authVersion: number;
  getHeaders: (includeContentType?: boolean) => Promise<Record<string, string>>;
  login: (email: string, password: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  signup: (email: string, password: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  signInWithGoogle: () => Promise<{
    data?: any;
    error?: any;
  }>;
  logout: () => Promise<boolean>;
  resetPassword: (email: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  updateUserProfile: (updates: Partial<UserProfile>) => Promise<{
    success: boolean;
    error?: string;
  }>;
  fetchSubscriptionStatus: () => Promise<SubscriptionStatus | null>;
  refreshSubscriptionStatus: () => Promise<void>;
  updateUserEmail: (email: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  updateUserJurisdiction: (jurisdiction: any) => Promise<{
    success: boolean;
    error?: string;
  }>;
}

// ------ Session Management ------
// Track session state to prevent duplication
let sessionInitialized = false;
let sessionInitPromise: Promise<any> | null = null;
let sessionOperationInProgress = false;
let validatedSessionIds = new Set<string>();

/**
 * Debug helper - logs auth state
 */
const logAuthState = (message: string, data?: any) => {
  const state = {
    sessionInitialized,
    sessionOperationInProgress,
    validatedSessionCount: validatedSessionIds.size,
    ...data
  };
  
  logger.debug('AuthContext', `${message}:`, state);
};

// ------ Context Creation ------
const AuthContext = createContext<AuthContextProps>({
  user: null,
  userProfile: null,
  session: null,
  sessionId: null,
  anonymousToken: null,
  loading: true,
  isSubscribed: false,
  isAdmin: false,
  isAuthenticated: false,
  subscriptionStatus: null,
  authVersion: 0,
  getHeaders: async () => ({}),
  login: async () => ({ success: false }),
  signup: async () => ({ success: false }),
  signInWithGoogle: async () => ({ data: undefined, error: undefined }),
  logout: async () => false,
  resetPassword: async () => ({ success: false }),
  updateUserProfile: async () => ({ success: false }),
  fetchSubscriptionStatus: async () => null,
  refreshSubscriptionStatus: async () => {},
  updateUserEmail: async () => ({ success: false }),
  updateUserJurisdiction: async () => ({ success: false }),
});

// ------ Auth Hook ------
export const useAuth = () => useContext(AuthContext);

// ------ Auth Provider Component ------
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [anonymousToken, setAnonymousToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [authVersion, setAuthVersion] = useState(0);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);

  const isAuthenticated = !!user && !!session;

  // Update auth version to trigger re-renders when auth state changes
  const incrementAuthVersion = useCallback(() => {
    setAuthVersion(prev => prev + 1);
    logger.debug('AuthContext', `Auth version incremented to ${authVersion + 1}`);
  }, [authVersion]);

  // ------ HTTP Headers Helper ------
  const getHeaders = async (includeContentType = true) => {
    const headers: Record<string, string> = {};
    
    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }
  
    // Try to get authenticated session first
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
  
    if (token) {
      // Authenticated user - use Supabase token
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      // Anonymous user - try token from localStorage
      const anonToken = localStorage.getItem('anonymous_token');
      
      if (anonToken) {
        headers['Authorization'] = `Bearer ${anonToken}`;
      } else {
        // Fallback to session ID if no token
        const anonSessionId = localStorage.getItem('anonymous_session_id');
        
        if (anonSessionId) {
          headers['X-Anonymous-Session'] = anonSessionId;
        }
      }
    }
  
    return headers;
  };

  // ------ Fetch Subscription Status ------
  const fetchSubscriptionStatus = async (): Promise<SubscriptionStatus | null> => {
    try {
      if (!userProfile) return null;
      
      logger.debug('AuthContext', 'Fetching subscription status');
      const headers = await getHeaders();
      
      const response = await fetch(`${API_URL}/user/subscription/status`, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        logger.error('AuthContext', `Error fetching subscription status: ${response.status}`);
        return null;
      }
      
      const data = await response.json();
      
      if (data.status === 'success' && data.data) {
        // Format the API response into our SubscriptionStatus type
        const status: SubscriptionStatus = {
          is_canceled: data.data.is_canceled || false,
          can_reactivate: data.data.can_reactivate || false,
          active_until: data.data.active_until || null,
          is_active: data.data.is_active || false,
          subscription_tier: data.data.subscription_tier || userProfile?.subscription_tier || 'free'
        };
        
        logger.debug('AuthContext', 'Subscription status:', status);
        
        // Update subscription status state
        setSubscriptionStatus(status);
        
        return status;
      }
      
      return null;
    } catch (error) {
      logger.error('AuthContext', 'Error fetching subscription status:', error);
      return null;
    }
  };
  
  // Refresh subscription status - returns void because we're setting state
  const refreshSubscriptionStatus = async () => {
    const status = await fetchSubscriptionStatus();
    if (status) {
      setSubscriptionStatus(status);
      
      // Update userProfile subscription info to match the status
      if (userProfile) {
        // Make sure to validate the subscription_tier value against allowed types
        const subscriptionTier = status.subscription_tier || userProfile.subscription_tier;
        let validatedTier: SubscriptionTier = 'free';
        
        // Validate the subscription tier to ensure it's one of the allowed values
        if (subscriptionTier === 'premium' || subscriptionTier === 'anonymous' || subscriptionTier === 'free') {
          validatedTier = subscriptionTier as SubscriptionTier;
        }
        
        // Create a type-safe updated profile
        const updatedProfile: UserProfile = {
          ...userProfile,
          subscription_tier: validatedTier,
          isSubscribed: status.is_active
        };
        
        setUserProfile(updatedProfile);
      }
      
      // Force re-render
      incrementAuthVersion();
    }
  };

  // ------ Fetch User Profile ------
  const fetchUserProfile = async () => {
    try {
      const headers = await getHeaders();
      logger.debug('AuthContext', 'Fetching user profile');
      
      const response = await fetch(`${API_URL}/user/me`, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        logger.error('AuthContext', `Error fetching user profile: ${response.status}`);
        return null;
      }
      
      const data = await response.json();
      
      if (data.status === 'success' && data.data?.user) {
        // Store session ID from backend if provided
        if (data.data.user.sessionId && !user) {
          const serverSessionId = data.data.user.sessionId;
          const currentSessionId = localStorage.getItem('anonymous_session_id');
          
          if (currentSessionId !== serverSessionId) {
            logger.debug('AuthContext', `Updating session ID from API: ${serverSessionId}`);
            localStorage.setItem('anonymous_session_id', serverSessionId);
            setSessionId(serverSessionId);
            
            // Track this as a validated session ID
            validatedSessionIds.add(serverSessionId);
          }
        }
        
        // Normalize profile data
        return normalizeUserProfile(data.data.user);
      }
      
      return null;
    } catch (error) {
      logger.error('AuthContext', 'Error in fetchUserProfile:', error);
      return null;
    }
  };

  // ------ Fetch Anonymous Token ------
  const fetchAnonymousToken = async (sessionId: string): Promise<string | null> => {
    try {
      logger.debug('AuthContext', `Fetching anonymous token for session ID: ${sessionId}`);
      
      const response = await fetch(`${API_URL}/auth/anonymous`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Anonymous-Session': sessionId
        },
        body: JSON.stringify({ sessionId })
      });
      
      if (!response.ok) {
        logger.error('AuthContext', `Error fetching anonymous token: ${response.status}`);
        return null;
      }
      
      const data = await response.json();
      
      if (data.status === 'success' && data.data?.token) {
        logger.debug('AuthContext', 'Successfully obtained anonymous token');
        
        // Store token in localStorage and state
        localStorage.setItem('anonymous_token', data.data.token);
        setAnonymousToken(data.data.token);
        
        // Update session ID if changed
        if (data.data.sessionId && data.data.sessionId !== sessionId) {
          logger.debug('AuthContext', `Server returned different session ID: ${data.data.sessionId}`);
          localStorage.setItem('anonymous_session_id', data.data.sessionId);
          setSessionId(data.data.sessionId);
          
          // Track this as a validated session ID
          validatedSessionIds.add(data.data.sessionId);
        } else {
          // Track original session ID as validated
          validatedSessionIds.add(sessionId);
        }
        
        return data.data.token;
      }
      
      logger.error('AuthContext', 'Unexpected response format:', data);
      return null;
    } catch (error) {
      logger.error('AuthContext', 'Error in fetchAnonymousToken:', error);
      return null;
    }
  };

  // ------ Authentication Methods ------
  // Google Sign In
  const signInWithGoogle = async () => {
    try {
      logger.debug('AuthContext', 'Starting Google sign in');
      const appUrl = import.meta.env.VITE_APP_URL;
      const redirectUrl = `${appUrl}/auth/callback`;
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
        }
      });
      
      if (error) {
        logger.error('AuthContext', 'Error in Google sign in:', error);
      } else {
        logger.debug('AuthContext', 'Google sign in initiated successfully');
      }
      
      return { data, error };
    } catch (error) {
      logger.error('AuthContext', 'Exception in signInWithGoogle:', error);
      if (error instanceof Error) {
        return { data: null, error };
      } else {
        return { data: null, error: new Error('Unknown error occurred') };
      }
    }
  };

  // Email/Password Login
  const login = async (email: string, password: string) => {
    try {
      logger.debug('AuthContext', `Attempting login for user: ${email}`);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        logger.error('AuthContext', 'Login error:', error.message);
        return { success: false, error: error.message };
      }

      logger.info('AuthContext', 'Login successful');
      incrementAuthVersion();
      
      // If we have a successful login, redirect to /chat
      if (data.session) {
        logger.info('AuthContext', 'Redirecting to /chat after successful login');
        
        // Use a short timeout to allow state updates to complete
        setTimeout(() => {
          window.location.href = '/chat';
        }, 100);
      }
      
      return { success: !!data.session };
    } catch (error: any) {
      logger.error('AuthContext', 'Exception in login:', error.message);
      return { success: false, error: error.message };
    }
  };

  // Email/Password Signup
  const signup = async (email: string, password: string) => {
    try {
      logger.debug('AuthContext', `Attempting signup for user: ${email}`);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) {
        logger.error('AuthContext', 'Signup error:', error.message);
        return { success: false, error: error.message };
      }

      logger.debug('AuthContext', 'Signup successful');
      incrementAuthVersion();
      
      return { success: !!data.user };
    } catch (error: any) {
      logger.error('AuthContext', 'Exception in signup:', error.message);
      return { success: false, error: error.message };
    }
  };

  // Logout
  const logout = async () => {
    try {
      logger.info('AuthContext', 'Logout process started');
      
      // Emit event before logout
      logger.info('AuthContext', 'Emitting BEFORE_LOGOUT event');
      AuthEvents.BEFORE_LOGOUT.emit();
      
      // Wait for event listeners to process
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Try to sign out through Supabase
      const { error } = await supabase.auth.signOut();
      
      // Even if there's an error with Supabase, we want to clear local state
      if (error) {
        logger.warn('AuthContext', 'Error during Supabase signOut, cleaning up local state anyway', error);
      }
      
      // Clear local state
      setUser(null);
      setSession(null);
      incrementAuthVersion();
      AuthEvents.AFTER_LOGOUT.emit();
      
      // Rather than immediate redirect, return success so Navigation component can handle it
      return true;
    } catch (error) {
      logger.error('AuthContext', 'Synchronous error during logout', error);
      // Still clean up local state even if there's an error
      setUser(null);
      setSession(null);
      incrementAuthVersion();
      return false;
    }
  };
  
  // Password Reset
  const resetPassword = async (email: string) => {
    try {
      logger.debug('AuthContext', `Requesting password reset for: ${email}`);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        logger.error('AuthContext', 'Reset password error:', error.message);
        return { success: false, error: error.message };
      }

      logger.debug('AuthContext', 'Reset password email sent successfully');
      return { success: true };
    } catch (error: any) {
      logger.error('AuthContext', 'Exception in resetPassword:', error.message);
      return { success: false, error: error.message };
    }
  };

  const updateUserEmail = async (newEmail: string): Promise<{ success: boolean; error?: string }> => {
    try {
      logger.debug('AuthContext', `Updating user email to: ${newEmail}`);
  
      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }
  
      const isOAuth = user.app_metadata?.provider && user.app_metadata.provider !== 'email';
      if (isOAuth) {
        return {
          success: false,
          error: `Email is managed by ${user.app_metadata.provider}. Update it from that provider.`
        };
      }
  
      const { error } = await supabase.auth.updateUser({
        email: newEmail
      });
  
      if (error) {
        logger.error('AuthContext', 'Error updating email via Supabase:', error.message);
        return { success: false, error: error.message };
      }
  
      logger.info('AuthContext', 'Verification email sent. Awaiting confirmation.');
      return { success: true };
    } catch (err: any) {
      logger.error('AuthContext', 'Unexpected error updating email:', err);
      return { success: false, error: err.message || 'Unknown error' };
    }
  };
  
  const updateUserJurisdiction = async (jurisdiction: any): Promise<{ success: boolean; error?: string }> => {
    try {
      if (!userProfile) {
        return { success: false, error: 'No user profile' };
      }
  
      // Create a new userProfile object with updated jurisdiction
      const updatedProfile: UserProfile = {
        ...userProfile,
        jurisdiction: jurisdiction
      };
  
      // Update the userProfile state
      setUserProfile(updatedProfile);
      
      // Force re-render
      incrementAuthVersion();
  
      return { success: true };
    } catch (error: any) {
      logger.error('AuthContext', 'Exception in updateUserJurisdiction:', error.message);
      return { success: false, error: error.message };
    }
  };

  // Update User Profile
  const updateUserProfile = async (updates: Partial<UserProfile>) => {
    if (!userProfile) {
      logger.error('AuthContext', 'Cannot update profile - no user profile');
      return { success: false, error: 'No user profile' };
    }

    try {
      logger.debug('AuthContext', 'Updating user profile', updates);
      const headers = await getHeaders();
      
      // Convert updates to API format
      const apiUpdateData = prepareProfileApiUpdate(updates);
      
      // Send profile update to API
      const response = await fetch(`${API_URL}/user/profile`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(apiUpdateData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        logger.error('AuthContext', 'Profile update failed:', errorData);
        return { success: false, error: errorData.message || 'Failed to update profile' };
      }
      
      // Update local state
      const updatedProfile = normalizeUserProfile({
        ...userProfile,
        ...updates
      });
      
      logger.debug('AuthContext', 'Profile updated successfully');
      setUserProfile(updatedProfile);
      incrementAuthVersion();

      return { success: true };
    } catch (error: any) {
      logger.error('AuthContext', 'Exception in updateUserProfile:', error.message);
      return { success: false, error: error.message };
    }
  };

  // ------ Anonymous Session Management ------
  /**
   * Ensures an anonymous session exists or creates a new one
   */
  const ensureAnonymousSession = async (forceNew = false): Promise<{sessionId: string, token: string | null} | null> => {
    // Prevent concurrent session operations
    if (sessionOperationInProgress) {
      logger.info('AuthContext', 'Session operation already in progress, waiting...');
      // Wait for current operation to complete
      await new Promise(resolve => setTimeout(resolve, 100));
      return ensureAnonymousSession(forceNew);
    }
    
    // Set operation flag
    sessionOperationInProgress = true;
    
    try {
      // First, try to use existing session
      if (!forceNew) {
        const existingSessionId = localStorage.getItem('anonymous_session_id');
        const existingToken = localStorage.getItem('anonymous_token');
        
        if (existingSessionId && existingToken) {
          logger.info('AuthContext', `Found existing session: ${existingSessionId.substring(0, 8)}...`);
          
          // Check token payload for session ID mismatch
          try {
            // Decode token (simple JWT parsing, not verification)
            const tokenParts = existingToken.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              const tokenUser = payload.user || {};
              const tokenSessionId = tokenUser.sessionId;
              
              // Check for session ID mismatch
              if (tokenSessionId && tokenSessionId !== existingSessionId) {
                logger.warn('AuthContext', `Token session ID (${tokenSessionId}) doesn't match localStorage (${existingSessionId})`);
                
                // Determine which one to use (prioritize token's session ID)
                logger.info('AuthContext', `Using token's session ID: ${tokenSessionId}`);
                localStorage.setItem('anonymous_session_id', tokenSessionId);
                setSessionId(tokenSessionId);
                validatedSessionIds.add(tokenSessionId);
                
                // Use updated session ID for the rest of the flow
                const updatedSessionId = tokenSessionId;
                
                // Update state with existing values
                setAnonymousToken(existingToken);
                
                // Check if this is a previously validated session ID
                const isPreValidated = validatedSessionIds.has(updatedSessionId);
                logger.info('AuthContext', `Session pre-validated: ${isPreValidated}`);
                
                // For pre-validated sessions, try to fetch profile directly
                if (isPreValidated) {
                  const profile = await fetchUserProfile();
                  
                  if (profile) {
                    logger.info('AuthContext', 'Pre-validated session confirmed with profile fetch');
                    
                    // Set profile state
                    setUserProfile({
                      ...profile,
                      isAnonymous: true,
                      subscription_tier: 'anonymous'
                    });
                    setIsAdmin(false);
                    
                    // Update auth state
                    incrementAuthVersion();
                    
                    return { sessionId: updatedSessionId, token: existingToken };
                  }
                  
                  logger.warn('AuthContext', 'Pre-validated session failed profile fetch');
                }
                
                // If not pre-validated or profile fetch failed, try to refresh token
                logger.info('AuthContext', 'Refreshing anonymous token');
                const refreshedToken = await fetchAnonymousToken(updatedSessionId);
                
                if (refreshedToken) {
                  // Try to fetch profile with new token
                  const profile = await fetchUserProfile();
                  
                  if (profile) {
                    logger.info('AuthContext', 'Session validated with refreshed token');
                    
                    // Set profile state
                    setUserProfile({
                      ...profile,
                      isAnonymous: true,
                      subscription_tier: 'anonymous'
                    });
                    setIsAdmin(false);
                    
                    // Update auth state
                    incrementAuthVersion();
                    
                    return { sessionId: updatedSessionId, token: refreshedToken };
                  }
                }
                
                logger.warn('AuthContext', 'Failed to validate existing session, creating new one');
                // Continue to create new session below
              } else {
                // No mismatch, continue with normal flow
                // Update state with existing values
                setSessionId(existingSessionId);
                setAnonymousToken(existingToken);
                
                // Check if this is a previously validated session ID
                const isPreValidated = validatedSessionIds.has(existingSessionId);
                logger.info('AuthContext', `Session pre-validated: ${isPreValidated}`);
                
                // For pre-validated sessions, try to fetch profile directly
                if (isPreValidated) {
                  const profile = await fetchUserProfile();
                  
                  if (profile) {
                    logger.info('AuthContext', 'Pre-validated session confirmed with profile fetch');
                    
                    // Set profile state
                    setUserProfile({
                      ...profile,
                      isAnonymous: true,
                      subscription_tier: 'anonymous'
                    });
                    setIsAdmin(false);
                    
                    // Update auth state
                    incrementAuthVersion();
                    
                    return { sessionId: existingSessionId, token: existingToken };
                  }
                  
                  logger.warn('AuthContext', 'Pre-validated session failed profile fetch');
                }
                
                // If not pre-validated or profile fetch failed, try to refresh token
                logger.info('AuthContext', 'Refreshing anonymous token');
                const refreshedToken = await fetchAnonymousToken(existingSessionId);
                
                if (refreshedToken) {
                  // Try to fetch profile with new token
                  const profile = await fetchUserProfile();
                  
                  if (profile) {
                    logger.info('AuthContext', 'Session validated with refreshed token');
                    
                    // Set profile state
                    setUserProfile({
                      ...profile,
                      isAnonymous: true,
                      subscription_tier: 'anonymous'
                    });
                    setIsAdmin(false);
                    
                    // Update auth state
                    incrementAuthVersion();
                    
                    return { sessionId: existingSessionId, token: refreshedToken };
                  }
                }
                
                logger.warn('AuthContext', 'Failed to validate existing session, creating new one');
                // Continue to create new session below
              }
            } else {
              logger.warn('AuthContext', 'Invalid token format in localStorage');
              // Continue with normal flow but will likely need to refresh token
              setSessionId(existingSessionId);
              setAnonymousToken(existingToken);
              
              // Try to refresh token
              const refreshedToken = await fetchAnonymousToken(existingSessionId);
              
              if (refreshedToken) {
                // Try to fetch profile with new token
                const profile = await fetchUserProfile();
                
                if (profile) {
                  logger.info('AuthContext', 'Session validated with refreshed token');
                  
                  // Set profile state
                  setUserProfile({
                    ...profile,
                    isAnonymous: true,
                    subscription_tier: 'anonymous'
                  });
                  setIsAdmin(false);
                  
                  // Update auth state
                  incrementAuthVersion();
                  
                  return { sessionId: existingSessionId, token: refreshedToken };
                }
              }
              
              logger.warn('AuthContext', 'Failed to validate existing session, creating new one');
              // Continue to create new session below
            }
          } catch (e) {
            logger.error('AuthContext', 'Error checking token payload:', e);
            // Continue with existing session ID as a fallback
            setSessionId(existingSessionId);
            setAnonymousToken(existingToken);
            
            // Try to refresh token as a fallback
            const refreshedToken = await fetchAnonymousToken(existingSessionId);
            
            if (refreshedToken) {
              // Try to fetch profile with new token
              const profile = await fetchUserProfile();
              
              if (profile) {
                logger.info('AuthContext', 'Session validated with refreshed token');
                
                // Set profile state
                setUserProfile({
                  ...profile,
                  isAnonymous: true,
                  subscription_tier: 'anonymous'
                });
                setIsAdmin(false);
                
                // Update auth state
                incrementAuthVersion();
                
                return { sessionId: existingSessionId, token: refreshedToken };
              }
            }
            
            logger.warn('AuthContext', 'Failed to validate existing session, creating new one');
            // Continue to create new session below
          }
        }
      }
      
      // If we get here, we need to create a new session
      logger.info('AuthContext', 'Creating new anonymous session');
      
      // Generate new session ID
      const newSessionId = crypto.randomUUID();
      
      // Store in localStorage and state
      localStorage.setItem('anonymous_session_id', newSessionId);
      setSessionId(newSessionId);
      
      // Fetch token for new session
      const token = await fetchAnonymousToken(newSessionId);
      
      if (token) {
        // Try to fetch profile
        const profile = await fetchUserProfile();
        
        if (profile) {
          setUserProfile({
            ...profile,
            isAnonymous: true,
            subscription_tier: 'anonymous'
          });
        } else {
          // Use default profile if fetch failed
          setUserProfile(createAnonymousProfile(newSessionId));
        }
        
        setIsAdmin(false);
        incrementAuthVersion();
        
        // Add to validated sessions
        validatedSessionIds.add(newSessionId);
        
        return { sessionId: newSessionId, token };
      }
      
      // If token fetch failed, use default profile
      setUserProfile(createAnonymousProfile(newSessionId));
      setIsAdmin(false);
      incrementAuthVersion();
      
      return { sessionId: newSessionId, token: null };
    } finally {
      // Clear operation flag
      sessionOperationInProgress = false;
    }
  };

  // ------ Session Change Handler ------
  const handleSessionChange = async (newSession: Session | null) => {
    logger.info('AuthContext', `Session change: ${newSession ? 'LOGIN' : 'LOGOUT'}`);
    
    // Update session state
    setSession(newSession);
    
    if (!newSession) {
      // User logged out - clear authenticated user state
      setUser(null);
      
      // Update auth state
      incrementAuthVersion();
      
      // Check if we have a valid anonymous session
      const existingSessionId = localStorage.getItem('anonymous_session_id');
      const existingToken = localStorage.getItem('anonymous_token');
      
      if (existingSessionId && existingToken && validatedSessionIds.has(existingSessionId)) {
        // Reuse validated session ID after logout
        logger.info('AuthContext', `Reusing previously validated session: ${existingSessionId}`);
        
        setSessionId(existingSessionId);
        setAnonymousToken(existingToken);
        
        // Try to fetch profile
        const profile = await fetchUserProfile();
        
        if (profile) {
          setUserProfile({
            ...profile,
            isAnonymous: true,
            subscription_tier: 'anonymous'
          });
          setIsAdmin(false);
          incrementAuthVersion();
          return;
        }
      }
      
      // Ensure anonymous session exists
      await ensureAnonymousSession(false);
    } else {
      // User logged in
      setUser(newSession.user);
      incrementAuthVersion();
      
      // Keep anonymous data in localStorage for later, but clear from state
      setAnonymousToken(null);
      
      // Fetch authenticated user profile
      const profile = await fetchUserProfile();
      
      if (profile) {
        // Set authenticated profile
        const tier = profile.subscription_tier || (profile.isSubscribed ? 'premium' : 'free');
        let validatedTier: SubscriptionTier = 'free';
        
        // Validate the subscription tier
        if (tier === 'premium' || tier === 'anonymous' || tier === 'free') {
          validatedTier = tier as SubscriptionTier;
        }
        
        const updatedProfile: UserProfile = {
          ...profile,
          isAnonymous: false,
          subscription_tier: validatedTier
        };
        
        setUserProfile(updatedProfile);
        setIsAdmin(updatedProfile.is_admin === true);
        incrementAuthVersion();
      }
    }
  };

  // ------ Initialize Auth State ------
  useEffect(() => {
    const initializeAuth = async () => {
      // Only initialize once
      if (sessionInitialized) {
        logger.info('AuthContext', 'Auth already initialized, skipping');
        return;
      }
      
      // Share initialization promise to prevent duplicates
      if (sessionInitPromise) {
        logger.info('AuthContext', 'Auth initialization in progress, waiting...');
        return sessionInitPromise;
      }
      
      // Create initialization promise
      sessionInitPromise = (async () => {
        logger.info('AuthContext', 'Initializing auth state...');
        logAuthState('Initial state');
        
        setLoading(true);
        
        try {
          // Store original session data
          const originalSessionId = localStorage.getItem('anonymous_session_id');
          const originalToken = localStorage.getItem('anonymous_token');
          
          // Check for existing Supabase session
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error) {
            logger.error('AuthContext', 'Error getting Supabase session:', error);
          }
          
          if (session) {
            // User is authenticated
            logger.info('AuthContext', 'Found authenticated session');
            await handleSessionChange(session);
            
            // Fetch subscription status after session is established
            await refreshSubscriptionStatus();
          } else {
            // User is not authenticated - handle anonymous session
            logger.info('AuthContext', 'No authenticated session found');
            
            // Try to validate existing session first
            await ensureAnonymousSession(false);
          }
          
          // Set up auth state change listener
          const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event, newSession) => {
              logger.info('AuthContext', `Auth state change event: ${event}`);
              
              if (event === 'USER_UPDATED') {
                logger.info('AuthContext', 'User updated event - possible email change');
                
                // Refresh user profile to get latest email
                if (newSession) {
                  const profile = await fetchUserProfile();
                  
                  if (profile) {
                    // Update user profile in state
                    setUserProfile(prevProfile => {
                      if (!prevProfile) return profile;
                      
                      return {
                        ...prevProfile,
                        ...profile
                      };
                    });
                    
                    incrementAuthVersion();
                  }
                }
              }

              // Skip duplicate initial session events
              if (event === 'INITIAL_SESSION' && authInitialized) {
                logger.info('AuthContext', 'Ignoring duplicate INITIAL_SESSION event');
                return;
              }
              
              await handleSessionChange(newSession);
              
              // Refresh subscription status on auth state change
              if (newSession) {
                await refreshSubscriptionStatus();
              }
            }
          );
          
          // Mark auth as initialized
          setAuthInitialized(true);
          sessionInitialized = true;
          
          logger.info('AuthContext', 'Auth initialization completed');
          logAuthState('Final state');
          
          // Return cleanup function
          return () => {
            logger.info('AuthContext', 'Cleaning up auth subscription');
            subscription.unsubscribe();
          };
        } catch (error) {
          logger.error('AuthContext', 'Error in auth initialization:', error);
          throw error;
        } finally {
          setLoading(false);
          sessionInitPromise = null;
        }
      })();
      
      return sessionInitPromise;
    };
    
    // Start initialization
    const cleanup = initializeAuth();
    
    // Return cleanup function
    return () => {
      cleanup.then(cleanupFn => {
        if (cleanupFn) cleanupFn();
      });
    };
  }, []);

  // ------ Update Admin Status ------
  useEffect(() => {
    if (userProfile) {
      setIsAdmin(userProfile.is_admin === true);
    } else {
      setIsAdmin(false);
    }
  }, [userProfile]);

  // ------ Compute Derived Properties ------
  // Update isSubscribed based on both userProfile and subscriptionStatus
  const isSubscribed = useMemo(() => {
    // If we have subscription status data, use it as the source of truth
    if (subscriptionStatus) {
      return subscriptionStatus.is_active;
    }
    
    // Otherwise fall back to the userProfile data
    return userProfile?.isSubscribed || userProfile?.subscription_tier === 'premium' || false;
  }, [userProfile, subscriptionStatus]);

  // Refresh subscription status when user profile changes
  useEffect(() => {
    if (userProfile && !userProfile.isAnonymous) {
      refreshSubscriptionStatus();
    }
  }, [userProfile?.id]);

  // ------ Context Value ------
  const value = {
    user,
    userProfile,
    session,
    sessionId,
    anonymousToken,
    loading,
    isSubscribed,
    isAdmin,
    isAuthenticated,
    subscriptionStatus,
    getHeaders,
    authVersion,
    login,
    signup,
    logout,
    signInWithGoogle,
    resetPassword,
    updateUserProfile,
    fetchSubscriptionStatus,
    refreshSubscriptionStatus,
    updateUserEmail,
    updateUserJurisdiction
  };

  // Log initialization for debugging
  useEffect(() => {
    logger.info('AuthContext', `Auth provider initialized with build ID: ${BUILD_ID}`);
    console.log('[AUTH] Build fingerprint:', BUILD_ID);
  }, []);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;